/**
 * Messages in the patient's language (amendment 25 §5, board `LangWhere`).
 *
 * **One module, because the language is one fact about a patient** and six
 * surfaces show it: the Languages card in AI Brain › General, the patient header,
 * the Patients list's bulk action, the character counter in both message editors,
 * the Communication log, and Triage call tasks. Written down six times it would
 * drift the way the recall call queue did.
 *
 * Two rules the spec is firm about, and they are enforced here rather than
 * remembered:
 *
 *   - **ELVA never guesses a language from a name.** There is no detector in this
 *     module and there must not be one. A language comes from the PMS or from a
 *     person setting it, and `LanguageSource` records which.
 *   - **A language the practice has not switched on sends English.** Not nothing:
 *     `effectiveLanguage()` falls back, so a patient is never skipped.
 */

export type LanguageCode = 'en' | 'es' | 'fa';

export interface Language {
  code: LanguageCode;
  /** In English, for the practice. */
  name: string;
  /** In the language, for the patient. */
  nativeName: string;
  /** Right to left — Farsi is, and the previews have to lay out that way. */
  rtl: boolean;
  textsAndEmails: boolean;
  /** Some languages cannot be called yet; the call is skipped and the text sent. */
  aiCalls: boolean;
  /** How many patients have it on file. */
  patients: number;
  /** English is always on and cannot be switched off. */
  alwaysOn?: boolean;
}

/**
 * A text message's length **depends on the alphabet**. GSM-7 fits 160 characters
 * per part; anything outside it (Persian, Arabic, Chinese, an emoji) forces UCS-2
 * and **70**. This is why the counter in the editors says *English 1 text · Farsi
 * 3 texts* rather than one number: one number would be wrong for one of them.
 */
export const CHARS_PER_TEXT: Record<LanguageCode, number> = { en: 160, es: 160, fa: 70 };

export const LANGUAGES: Language[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    rtl: false,
    textsAndEmails: true,
    aiCalls: true,
    patients: 3902,
    alwaysOn: true,
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    rtl: false,
    textsAndEmails: true,
    aiCalls: true,
    patients: 418,
  },
  {
    code: 'fa',
    name: 'Farsi',
    nativeName: 'فارسی',
    rtl: true,
    textsAndEmails: true,
    // Not yet — so a Farsi post-op call is skipped and the text version sent.
    aiCalls: false,
    patients: 86,
  },
];

/** Which languages the practice has switched on. English can never be off. */
export const languageStore: { on: LanguageCode[] } = { on: ['en', 'es', 'fa'] };

export function languageByCode(code: LanguageCode): Language | undefined {
  return LANGUAGES.find((l) => l.code === code);
}

export function languageName(code: LanguageCode): string {
  return languageByCode(code)?.name ?? code;
}

export function isRtl(code: LanguageCode): boolean {
  return languageByCode(code)?.rtl ?? false;
}

export function isLanguageOn(code: LanguageCode): boolean {
  return code === 'en' || languageStore.on.includes(code);
}

export function setLanguageOn(code: LanguageCode, on: boolean): void {
  if (code === 'en') return; // always on
  languageStore.on = on
    ? [...new Set([...languageStore.on, code])]
    : languageStore.on.filter((c) => c !== code);
}

/** The languages a message is actually sent in — what the counter iterates. */
export function activeLanguages(): Language[] {
  return LANGUAGES.filter((l) => isLanguageOn(l.code));
}

/**
 * What this patient will really get. A language that is not switched on falls
 * back to English rather than to silence (§5) — *the patient is never skipped*.
 */
export function effectiveLanguage(preferred: LanguageCode): LanguageCode {
  return isLanguageOn(preferred) ? preferred : 'en';
}

/** Can ELVA place a call in this language? If not, the text version goes. */
export function canCallIn(code: LanguageCode): boolean {
  return languageByCode(effectiveLanguage(code))?.aiCalls ?? false;
}

/* ---- where a patient's language came from -------------------------------- */

export type LanguageSource =
  | { kind: 'pms' }
  | { kind: 'staff'; by: string; on: string }
  | { kind: 'default' };

/** The line under the select on the patient page. */
export function languageSourceLabel(source: LanguageSource): string {
  if (source.kind === 'pms') return 'From the PMS';
  if (source.kind === 'staff') return `Set by ${source.by}, ${source.on}`;
  return 'Default';
}

/**
 * The nudge on a patient page: *Wrote in Farsi on 12 Sep. Make Farsi their
 * language?* It is a **suggestion from something the patient actually wrote**,
 * never from their name — and it only exists when there is such a message.
 */
export interface LanguageNudge {
  code: LanguageCode;
  /** When they wrote in it. */
  on: string;
  /** `their` / `his` / `her` — the mockup says *his* for Reza. */
  possessive: string;
}

export function nudgeSentence(n: LanguageNudge): string {
  return `Wrote in ${languageName(n.code)} on ${n.on}. Make ${languageName(n.code)} ${n.possessive} language?`;
}

/* ---- counting ------------------------------------------------------------ */

export interface TextCount {
  code: LanguageCode;
  name: string;
  chars: number;
  texts: number;
}

/**
 * One count per language the practice uses (§3). The opt-out line counts,
 * because the patient's phone counts it.
 */
export function textCountsByLanguage(body: string, optOutLine: string): TextCount[] {
  const chars = body.length + (optOutLine === '' ? 0 : optOutLine.length + 1);
  return activeLanguages().map((l) => ({
    code: l.code,
    name: l.name,
    chars,
    texts: Math.max(1, Math.ceil(chars / CHARS_PER_TEXT[l.code])),
  }));
}

/* ---- the lines we translate ourselves ------------------------------------ */

/**
 * Opt-out, confirm and safety lines use **our own reviewed translations** (§5),
 * not the model's — they are legal or clinical text, and a translation that
 * drifts is a different promise. ELVA adds them; nobody writes them.
 */
export const REVIEWED_LINES: Record<
  'opt_out' | 'confirm_text' | 'post_op_safety',
  Record<LanguageCode, string>
> = {
  opt_out: {
    en: 'Reply STOP to opt out',
    es: 'Responde STOP para no recibir más mensajes',
    fa: 'برای لغو پیام‌ها STOP را بفرستید',
  },
  confirm_text: {
    en: 'Reply C to confirm or R to change the time.',
    es: 'Responde C para confirmar o R para cambiar la hora.',
    fa: 'برای تأیید C و برای تغییر زمان R را بفرستید.',
  },
  post_op_safety: {
    en: 'If it gets worse, call us or 911 now — don’t wait for us to call you.',
    es: 'Si empeora, llámanos o al 911 ahora — no esperes a que te llamemos.',
    fa: 'اگر بدتر شد، همین حالا با ما یا ۹۱۱ تماس بگیرید — منتظر تماس ما نمانید.',
  },
};

/** The short form of the safety line, as the locked chip shows it. */
export const POST_OP_SAFETY_CHIP = '“If it gets worse, call us or 911 now”';

export function reviewedLine(
  key: keyof typeof REVIEWED_LINES,
  code: LanguageCode,
): string {
  return REVIEWED_LINES[key][effectiveLanguage(code)];
}
